import { useTranslation } from "react-i18next";
import {
  Button,
  Flex,
  Form,
  InputNumber,
  message,
  Radio,
  Switch,
  Tooltip,
} from "antd";
import CustomCard from "../../../../custom-card/CustomCard";
import { InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";
import FormItemDropdown from "../../../../form-dropdown/FormItemDropdown";
import "./attendance-setting.scss";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCourseAttendanceSetting,
  postMoocAttendanceSetting,
} from "../../../../../service/courses";

type AttendanceSettingProps = {
  dataUnit: any;
};

const validations = {
  unit: {
    required: true,
    validator: (_: unknown, value: number[]) => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return Promise.reject(new Error("Không được bỏ trống"));
      }
      return Promise.resolve();
    },
  },
  absences: {
    required: true,
    validator: (_: unknown, value: string) => {
      if (value === undefined || value === null) {
        return Promise.reject(new Error("Không được bỏ trống"));
      }
      return Promise.resolve();
    },
  },
};

const AttendanceSetting = ({ dataUnit }: AttendanceSettingProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [isCustom, setIsCustom] = useState(false);
  const [maxTimesAllow, setMaxTimesAllow] = useState(0);
  const [listUnit, setListUnit] = useState<any>();
  const selectedUnit = Form.useWatch("selectedUnit", form);
  const modeCardValue = Form.useWatch("modeCard", form);

  // handle finish form
  const onFinish = async (values: any) => {
    const payload = {
      courseId: Number(id),
      isEnabled: values.modeCard,
      form: values.attendanceType,
      allowedAbsentTime: values.timesOfAllowedAbsences,
      isWholeCourse: !isCustom,
      unitIds: isCustom ? values.selectedUnit : null,
    };

    await postMoocAttendanceSetting(payload).then((res) => {
      if (res.status === 200) {
        message.success(t("Lưu thành công!", "Lưu thành công!"));
        getAttendanceSetting();
      }
    });
  };

  // get setting course
  const getAttendanceSetting = async () => {
    await getCourseAttendanceSetting(id).then((res) => {
      if (res.status === 200) {
        const { data } = res.data;
        form.setFieldsValue({
          modeCard: data.isEnabled ? data.isEnabled : false,
          attendanceType: data.form ? data.form : 1,
          selectedUnitOptions:
            data.isWholeCourse == null ? "1" : data.isWholeCourse ? "1" : "2",
          selectedUnit: data.unitIds ? data.unitIds : null,
          timesOfAllowedAbsences: data.allowedAbsentTime
            ? data.allowedAbsentTime
            : 0,
        });
        setIsCustom(data.isWholeCourse == null ? false : !data.isWholeCourse);
      }
    });
  };

  // useEffect
  useEffect(() => {
    getAttendanceSetting();
  }, []);

  useEffect(() => {
    if (isCustom) {
      if (selectedUnit && selectedUnit.length > 0) {
        setMaxTimesAllow(selectedUnit?.length - 1);
      } else {
        setMaxTimesAllow(0);
      }
    } else {
      if (dataUnit && dataUnit.length > 0) {
        setMaxTimesAllow(dataUnit?.length - 1);
      } else {
        setMaxTimesAllow(0);
      }
    }
  }, [isCustom, selectedUnit]);

  useEffect(() => {
    if (dataUnit.length > 0) {
      const data = dataUnit.map((item: any) => {
        return {
          code: item.id,
          name: item.name,
        };
      });
      setListUnit(data);
    }
  }, [dataUnit]);

  return (
    <div className="attendance-setting">
      <section className="setting-course">
        <div className="attendance-setting__form">
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              modeCard: false,
              attendanceType: 1,
            }}
          >
            <CustomCard
              className="attendance-card-setting mode-card wrapper-settings--card"
              title={t("Chế độ điểm danh", "Chế độ điểm danh")}
              extra={
                <Form.Item name="modeCard" style={{ marginBottom: 0 }}>
                  <Switch defaultChecked />
                </Form.Item>
              }
            >
              <div className="mode-card__description">
                <p className="mode-card__description__text">
                  Học viên được yêu cầu thực hiện điểm danh khi bật chế độ này.
                  Sau 10 giây truy cập học liệu, hệ thống sẽ tự động thực hiện
                  việc điểm danh. Mỗi học liệu chỉ được điểm danh 1 lần duy nhất
                  tại lần đầu tiên học viên truy cập học liệu
                </p>
              </div>
            </CustomCard>
            {modeCardValue ? (
              <>
                <CustomCard
                  className="attendance-card-setting type-card wrapper-settings--card"
                  title={t("Hình thức điểm danh", "Hình thức điểm danh")}
                >
                  <Form.Item name="attendanceType">
                    <Radio.Group
                      defaultValue={1}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                      options={[
                        {
                          value: 1,
                          label: (
                            <Flex gap="small">
                              {t("Câu hỏi điểm danh", "Câu hỏi điểm danh")}
                              <Tooltip
                                placement="right"
                                title={t("Hiển thị popup yêu cần học viên xác nhận điểm danh", "Hiển thị popup yêu cần học viên xác nhận điểm danh")}
                              >
                                <InfoCircleOutlined />
                              </Tooltip>
                            </Flex>
                          ),
                        },
                        { value: 2, label: `Sử dụng khuôn mặt` },
                      ]}
                    />
                  </Form.Item>
                </CustomCard>
                <CustomCard
                  className="attendance-card-setting action-card wrapper-settings--card"
                  title={
                    <>
                      <span style={{ marginRight: 10 }}>
                        {t("Thực hiện điểm danh", "Thực hiện điểm danh")}
                      </span>
                      <Tooltip
                        placement="right"
                        title={t("Không bao gồm các học liệu đánh giá", "Không bao gồm các học liệu đánh giá")}
                        className="unit-description"
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </>
                  }
                >
                  <Flex gap="small" className="flex-unit">
                    <Form.Item name="selectedUnitOptions">
                      <Radio.Group
                        defaultValue="1"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                        options={[
                          { value: "1", label: `Tất cả học liệu` },
                          { value: "2", label: `Tùy chọn` },
                        ]}
                        onChange={(e) => {
                          setIsCustom(e.target.value !== "1");
                          form.setFieldValue("timesOfAllowedAbsences", null);
                        }}
                      />
                    </Form.Item>
                  </Flex>
                  {isCustom ? (
                    <Flex
                      gap="small"
                      className="flex-unit"
                      style={{ marginBottom: 45 }}
                    >
                      <Form.Item
                        name="selectedUnit"
                        rules={[validations.unit]}
                        className="attendance-unit"
                      >
                        <FormItemDropdown
                          options={listUnit}
                          className="mr-2"
                          placeholder={t("Chọn học liệu", "Chọn học liệu")}
                          modeDropdown="multiple"
                          style={{ minWidth: 300 }}
                          onChange={() =>
                            form.setFieldValue("timesOfAllowedAbsences", null)
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Flex>
                  ) : null}
                  <Flex className="flex-absences" gap="small">
                    <span className="mr-2 absences-title">
                      {t("Số lần vắng cho phép", "Số lần vắng cho phép")}
                    </span>
                    <Form.Item
                      className="absences-input"
                      name="timesOfAllowedAbsences"
                      rules={[validations.absences]}
                    >
                      <InputNumber
                        className="mr-2"
                        min={0}
                        max={maxTimesAllow}
                      />
                    </Form.Item>
                    <span className="absences-unit">{t("lần/môn học", "lần/môn học")}</span>
                    <Tooltip
                      placement="right"
                      title={
                        <>
                          {t("Phải ít hơn số học liệu yêu cầu điểm danh (tối đa", "Phải ít hơn số học liệu yêu cầu điểm danh (tối đa")}{" "}
                          {maxTimesAllow})
                        </>
                      }
                      className="absences-tooltip"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Flex>
                </CustomCard>
              </>
            ) : null}

            <Flex className="mt-4 mb-4" align="center" gap={16}>
              <Button
                htmlType="submit"
                icon={<SaveOutlined />}
                className="btn-primary"
              >
                {t("Lưu", "Lưu")}
              </Button>
              <Button className="btn-secondary" onClick={getAttendanceSetting}>
                {t("Huỷ", "Huỷ")}
              </Button>
            </Flex>
          </Form>
        </div>
      </section>
    </div>
  );
};

export default AttendanceSetting;
